import React, { useState, useEffect } from 'react';
import {
    AlertTriangle,
    CheckCircle,
    XCircle,
    Activity,
    Database,
    Layers,
    MapPin,
    Package,
    ArrowLeft,
    ChevronDown,
    ChevronUp,
    TrendingUp,
    AlertCircle,
    Lightbulb,
    Search,
    RefreshCw
} from 'lucide-react';
import {
    putAwayDiagnosticsService,
    PutAwayDiagnosticsRequest,
    PutAwayDiagnosticsResponse,
    ItemValidationResult,
    ItemBinSizeValidationResult,
    StcBinValidationResult,
    StcBinTypeValidationResult,
    StcBinGrpValidationResult,
    StcMovementPriorityValidationResult,
    AlgorithmSimulationResult,
    ProposedBinAllocation,
    ActiveBinGroupInfo
} from '../../../../services/putAwayDiagnosticsService';
import { warehouseService } from '../../../../services/warehouseService';
import { WarehouseOption } from '../../../../types/warehouse';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PutAwayDiagnosticsProps {
    onBack: () => void;
}

export const PutAwayDiagnostics: React.FC<PutAwayDiagnosticsProps> = ({ onBack }) => {
    const { t } = useTranslation();
    // Form state
    const [itemNo, setItemNo] = useState('');
    const [warehouseId, setWarehouseId] = useState('');
    const [quantity, setQuantity] = useState<number>(1);
    const [binGrpNo, setBinGrpNo] = useState<string>('');
    const [grShipmentNo, setGrShipmentNo] = useState<string>('');
    const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

    // UI state
    const [loading, setLoading] = useState(false);
    const [loadingWarehouses, setLoadingWarehouses] = useState(true);
    const [error, setError] = useState<string>('');
    const [results, setResults] = useState<PutAwayDiagnosticsResponse | null>(null);

    // Collapsible sections
    const [expandedSections, setExpandedSections] = useState<{
        masterData: boolean;
        algorithm: boolean;
        recommendations: boolean;
    }>({
        masterData: true,
        algorithm: true,
        recommendations: true
    });

    useEffect(() => {
        loadWarehouses();
    }, []);

    const loadWarehouses = async () => {
        try {
            setLoadingWarehouses(true);
            const warehouseOptions = await warehouseService.getWarehouseOptions();
            setWarehouses(warehouseOptions);
            if (warehouseOptions.length > 0) {
                setWarehouseId(warehouseOptions[0].value);
            }
        } catch (err) {
            console.error('Error loading warehouses:', err);
            setError(t('manager.modules.adminHost.putAwayDiag.loadWarehousesError', {}, 'Kunne ikke laste lagre. Vennligst prøv igjen.'));
        } finally {
            setLoadingWarehouses(false);
        }
    };

    const handleRunDiagnostics = async () => {
        if (!itemNo.trim()) {
            setError(t('manager.modules.adminHost.putAwayDiag.itemRequired', {}, 'Varenummer må fylles ut'));
            return;
        }

        if (!warehouseId) {
            setError(t('manager.modules.adminHost.putAwayDiag.warehouseRequired', {}, 'Lager må velges'));
            return;
        }

        if (quantity <= 0) {
            setError(t('manager.modules.adminHost.putAwayDiag.quantityPositive', {}, 'Antall må være større enn 0'));
            return;
        }

        try {
            setLoading(true);
            setError('');
            setResults(null);

            const request: PutAwayDiagnosticsRequest = {
                itemNo: itemNo.trim(),
                warehouseId,
                quantity,
                binGrpNo: binGrpNo.trim() ? parseInt(binGrpNo.trim()) : undefined,
                grShipmentNo: grShipmentNo.trim() ? parseInt(grShipmentNo.trim()) : 0
            };

            const response = await putAwayDiagnosticsService.diagnose(request);
            setResults(response);

            // Auto-expand all sections when results are received
            setExpandedSections({
                masterData: true,
                algorithm: true,
                recommendations: true
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manager.modules.adminHost.putAwayDiag.runError', {}, 'En feil oppstod ved kjøring av diagnostikk'));
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const renderInputForm = () => (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('manager.modules.adminHost.putAwayDiag.parametersTitle', {}, 'Diagnostikkparametere')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.general.itemNumber', {}, 'Varenummer')} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={itemNo}
                        onChange={(e) => setItemNo(e.target.value)}
                        placeholder={t('manager.modules.adminHost.putAwayDiag.itemPlaceholder', {}, 'F.eks. 10000')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={loading}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.general.warehouse', {}, 'Lager')} <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={warehouseId}
                        onChange={(e) => setWarehouseId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={loading || loadingWarehouses}
                    >
                        {loadingWarehouses ? (
                            <option>{t('manager.modules.adminHost.putAwayDiag.loadingWarehouses', {}, 'Laster lagre...')}</option>
                        ) : warehouses.length === 0 ? (
                            <option>{t('manager.modules.adminHost.putAwayDiag.noWarehouses', {}, 'Ingen lagre tilgjengelig')}</option>
                        ) : (
                            warehouses.map(wh => (
                                <option key={wh.value} value={wh.value}>
                                    {wh.label}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.fields.quantity', {}, 'Antall')} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={loading}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('manager.modules.adminHost.putAwayDiag.binGrpLabel', {}, 'Lokasjonsgruppenummer (valgfritt)')}
                    </label>
                    <input
                        type="number"
                        value={binGrpNo}
                        onChange={(e) => setBinGrpNo(e.target.value)}
                        placeholder={t('manager.modules.adminHost.putAwayDiag.binGrpPlaceholder', {}, 'La stå tom for å bruke varens standard')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={loading}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('manager.modules.adminHost.putAwayDiag.grShipmentLabel', {}, 'GR Forsendelsesnr (valgfritt)')}
                    </label>
                    <input
                        type="number"
                        value={grShipmentNo}
                        onChange={(e) => setGrShipmentNo(e.target.value)}
                        placeholder={t('manager.modules.adminHost.putAwayDiag.grShipmentPlaceholder', {}, 'For batch-varer, standard 0')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="mt-4">
                <button
                    onClick={handleRunDiagnostics}
                    disabled={loading || loadingWarehouses}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {loading ? (
                        <>
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            {t('manager.modules.adminHost.putAwayDiag.running', {}, 'Kjører diagnostikk...')}
                        </>
                    ) : (
                        <>
                            <Search className="w-5 h-5" />
                            {t('manager.modules.adminHost.putAwayDiag.run', {}, 'Kjør diagnostikk')}
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    const renderSummaryBar = () => {
        if (!results) return null;

        return (
            <div className={`rounded-lg p-6 shadow-sm border-2 ${results.isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-3">
                    {results.isValid ? (
                        <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                    ) : (
                        <XCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                        <h3 className={`text-lg font-semibold ${results.isValid ? 'text-green-900' : 'text-red-900'}`}>
                            {results.isValid ? t('manager.modules.adminHost.putAwayDiag.masterDataValid', {}, 'Grunndata er korrekt konfigurert') : t('manager.modules.adminHost.putAwayDiag.masterDataInvalid', {}, 'Grunndata har mangler')}
                        </h3>
                        <p className={`mt-1 ${results.isValid ? 'text-green-700' : 'text-red-700'}`}>
                            {results.summary}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const renderValidationCard = (
        title: string,
        icon: React.ReactNode,
        validation: ItemValidationResult | ItemBinSizeValidationResult | StcBinValidationResult | StcBinTypeValidationResult | StcBinGrpValidationResult | StcMovementPriorityValidationResult,
        isHighPriority: boolean = false
    ) => {
        const hasIssues = validation.issues.length > 0;
        const hasWarnings = validation.warnings.length > 0;
        const isValid = !hasIssues;

        return (
            <div className={`bg-white rounded-lg p-4 shadow-sm border-2 ${
                isHighPriority && hasIssues ? 'border-red-400 ring-2 ring-red-200' :
                hasIssues ? 'border-red-200' :
                hasWarnings ? 'border-yellow-200' :
                'border-green-200'
            }`}>
                <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                        isHighPriority && hasIssues ? 'bg-red-100' :
                        hasIssues ? 'bg-red-50' :
                        hasWarnings ? 'bg-yellow-50' :
                        'bg-green-50'
                    }`}>
                        {icon}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{title}</h3>
                            {isHighPriority && (
                                <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded">
                                    {t('manager.modules.adminHost.putAwayDiag.critical', {}, 'KRITISK')}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            {isValid ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                                <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className={`text-sm ${isValid ? 'text-green-700' : 'text-red-700'}`}>
                                {isValid ? t('manager.modules.adminHost.putAwayDiag.ok', {}, 'OK') : t('manager.modules.adminHost.putAwayDiag.errorsFound', {}, 'Feil funnet')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Issues */}
                {validation.issues.length > 0 && (
                    <div className="space-y-1 mb-2">
                        {validation.issues.map((issue, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 p-2 rounded">
                                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{issue}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Warnings */}
                {validation.warnings.length > 0 && (
                    <div className="space-y-1">
                        {validation.warnings.map((warning, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{warning}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Details based on type */}
                {isValid && renderValidationDetails(validation)}
            </div>
        );
    };

    const renderValidationDetails = (validation: any) => {
        // Items details
        if ('itemNo' in validation && validation.itemNo) {
            return (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.itemNo', {}, 'Varenr:')}</span> {validation.itemNo}</div>
                    {validation.description && <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.description', {}, 'Beskrivelse:')}</span> {validation.description}</div>}
                    {validation.binGrpNo !== undefined && <div><span className="font-medium">BinGrpNo:</span> {validation.binGrpNo}</div>}
                </div>
            );
        }

        // ItemBinSize details
        if ('recordCount' in validation && validation.records) {
            return (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.recordCount', {}, 'Antall poster:')}</span> {validation.recordCount}</div>
                    {validation.records.length > 0 && (
                        <div className="mt-2 space-y-1">
                            {validation.records.map((record: any, idx: number) => (
                                <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                                    <span className="font-medium">BinSize {record.binSizeNo}</span>
                                    {record.binSizeDescription && <span> - {record.binSizeDescription}</span>}
                                    <span> ({t('manager.modules.adminHost.putAwayDiag.details.maxPriority', { '0': record.maxQuantity, '1': record.putAwayPriorityNo }, `Maks: ${record.maxQuantity}, Prioritet: ${record.putAwayPriorityNo}`)})</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // StcBin details
        if ('totalBinsInWarehouse' in validation) {
            return (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.totalBins', {}, 'Totalt lokasjoner:')}</span> {validation.totalBinsInWarehouse}</div>
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.matchingBinGrp', {}, 'Matcher BinGrp:')}</span> {validation.binsMatchingBinGrp}</div>
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.availableBins', {}, 'Tilgjengelige:')}</span> {validation.availableBins?.length || 0}</div>
                </div>
            );
        }

        // BinType details
        if ('binTypes' in validation && validation.binTypes) {
            return (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.typeCount', {}, 'Antall typer:')}</span> {validation.recordCount}</div>
                </div>
            );
        }

        // BinGrp details
        if ('binGrpNo' in validation && validation.binGrpNo !== undefined) {
            const binGrpVal = validation as StcBinGrpValidationResult;
            return (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2 text-sm text-gray-600">
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.groupNo', {}, 'Gruppe nr:')}</span> {binGrpVal.binGrpNo}</div>
                    {binGrpVal.description && <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.description', {}, 'Beskrivelse:')}</span> {binGrpVal.description}</div>}

                    {/* Active bin groups — all groups considered by the SP */}
                    {binGrpVal.activeBinGroups && binGrpVal.activeBinGroups.length > 0 && (
                        <div className="mt-2">
                            <div className="font-medium text-gray-700 mb-1">
                                {t('manager.modules.adminHost.putAwayDiag.details.activeGroupsInSp', { '0': binGrpVal.activeBinGroups.length }, `Aktive grupper i SP (${binGrpVal.activeBinGroups.length} stk):`)}
                            </div>
                            <div className="space-y-1">
                                {binGrpVal.activeBinGroups.map((grp: ActiveBinGroupInfo) => (
                                    <div key={grp.binGrpNo} className={`flex items-center justify-between p-1.5 rounded text-xs ${
                                        grp.isItemOwnGroup ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                                    }`}>
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{grp.binGrpNo}</span>
                                            {grp.isItemOwnGroup && (
                                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">
                                                    {t('manager.modules.adminHost.putAwayDiag.details.itemOwnGroup', {}, 'VARENS GRUPPE')}
                                                </span>
                                            )}
                                            {grp.commonAvailable && !grp.isItemOwnGroup && (
                                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold">
                                                    COMMON
                                                </span>
                                            )}
                                            {grp.description && <span className="text-gray-500">{grp.description}</span>}
                                        </div>
                                        <span className="text-gray-500">{t('manager.modules.adminHost.putAwayDiag.details.binsCount', { '0': grp.binCount }, `${grp.binCount} bins`)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Movement Priority details
        if ('priorities' in validation && validation.priorities) {
            return (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.details.priorityCount', {}, 'Antall prioriteter:')}</span> {validation.recordCount}</div>
                </div>
            );
        }

        return null;
    };

    const renderMasterDataPanel = () => {
        if (!results) return null;

        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                    onClick={() => toggleSection('masterData')}
                    className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Database className="w-6 h-6 text-blue-600" />
                        <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminHost.putAwayDiag.masterDataValidation', {}, 'Grunndata-validering')}</h2>
                    </div>
                    {expandedSections.masterData ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                </button>

                {expandedSections.masterData && (
                    <div className="p-6 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderValidationCard(
                            'Items',
                            <Package className="w-5 h-5 text-blue-600" />,
                            results.itemValidation
                        )}
                        {renderValidationCard(
                            'ItemBinSize',
                            <Layers className="w-5 h-5 text-orange-600" />,
                            results.itemBinSizeValidation,
                            true // Mark as high priority
                        )}
                        {renderValidationCard(
                            'StcBin',
                            <MapPin className="w-5 h-5 text-green-600" />,
                            results.stcBinValidation
                        )}
                        {renderValidationCard(
                            'StcBinType',
                            <Database className="w-5 h-5 text-purple-600" />,
                            results.stcBinTypeValidation
                        )}
                        {renderValidationCard(
                            'StcBinGrp',
                            <Layers className="w-5 h-5 text-teal-600" />,
                            results.stcBinGrpValidation
                        )}
                        {renderValidationCard(
                            'StcMovementPriority',
                            <TrendingUp className="w-5 h-5 text-indigo-600" />,
                            results.movementPriorityValidation
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderAlgorithmPanel = () => {
        if (!results || !results.algorithmSimulation) return null;

        const simulation = results.algorithmSimulation;

        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                    onClick={() => toggleSection('algorithm')}
                    className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Activity className="w-6 h-6 text-purple-600" />
                        <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminHost.putAwayDiag.algorithmSimulation', {}, 'Algoritme-simulering')}</h2>
                    </div>
                    {expandedSections.algorithm ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                </button>

                {expandedSections.algorithm && (
                    <div className="p-6 pt-0 space-y-6">
                        {/* Proposed Distribution - NEW SECTION */}
                        {simulation.proposedDistribution && simulation.proposedDistribution.length > 0 && (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
                                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-lg">
                                    <Layers className="w-6 h-6 text-blue-600" />
                                    {t('manager.modules.adminHost.putAwayDiag.proposedDistribution', { '0': simulation.requestedQuantity }, `Foreslått fordeling av ${simulation.requestedQuantity} enheter`)}
                                </h3>

                                {/* Summary Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                                        <div className="text-sm text-gray-600">{t('manager.modules.adminHost.putAwayDiag.requested', {}, 'Etterspurt')}</div>
                                        <div className="text-2xl font-bold text-gray-900">{simulation.requestedQuantity}</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 border border-green-200">
                                        <div className="text-sm text-gray-600">{t('manager.modules.adminHost.putAwayDiag.canBePlaced', {}, 'Kan plasseres')}</div>
                                        <div className="text-2xl font-bold text-green-600">{simulation.totalAllocated}</div>
                                    </div>
                                    <div className={`bg-white rounded-lg p-4 border ${simulation.remainingUnallocated > 0 ? 'border-red-200' : 'border-gray-200'}`}>
                                        <div className="text-sm text-gray-600">{t('manager.modules.adminHost.putAwayDiag.toFallback', {}, 'Til [X] fallback')}</div>
                                        <div className={`text-2xl font-bold ${simulation.remainingUnallocated > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                            {simulation.remainingUnallocated}
                                        </div>
                                    </div>
                                </div>

                                {/* Distribution Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="text-left text-sm text-gray-600 border-b border-blue-200">
                                                <th className="pb-2 font-semibold">#</th>
                                                <th className="pb-2 font-semibold">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                                                <th className="pb-2 font-semibold text-right">{t('common.fields.quantity', {}, 'Antall')}</th>
                                                <th className="pb-2 font-semibold text-right">{t('manager.modules.adminHost.putAwayDiag.table.capacity', {}, 'Kapasitet')}</th>
                                                <th className="pb-2 font-semibold">{t('common.fields.type', {}, 'Type')}</th>
                                                <th className="pb-2 font-semibold">{t('manager.modules.adminHost.putAwayDiag.table.algorithmStep', {}, 'Algoritme-steg')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {simulation.proposedDistribution.map((alloc, idx) => (
                                                <tr key={idx} className={`border-b border-blue-100 ${
                                                    alloc.isFallback ? 'bg-red-50' : 'bg-white'
                                                }`}>
                                                    <td className="py-3 font-semibold text-gray-700">{alloc.order}</td>
                                                    <td className="py-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`font-mono font-bold ${
                                                                alloc.isFallback ? 'text-red-700' : 'text-blue-700'
                                                            }`}>
                                                                {alloc.binCode}
                                                            </span>
                                                            {alloc.stcZoneID && (
                                                                <span className="text-xs text-gray-500">
                                                                    ({alloc.stcZoneID})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 text-right">
                                                        <span className={`font-bold ${
                                                            alloc.isFallback ? 'text-red-700' : 'text-green-700'
                                                        }`}>
                                                            {alloc.allocatedQuantity}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 text-right text-sm text-gray-600">
                                                        {!alloc.isFallback && (
                                                            <>
                                                                {alloc.currentBinQuantity} / {alloc.binMaxCapacity}
                                                                <div className="text-xs text-gray-400">
                                                                    {t('manager.modules.adminHost.putAwayDiag.table.free', { '0': alloc.availableCapacity }, `Ledig: ${alloc.availableCapacity}`)}
                                                                </div>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="py-3">
                                                        <div className="flex flex-wrap gap-1">
                                                            {alloc.isFallback && (
                                                                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                                                    {t('manager.modules.adminHost.putAwayDiag.badges.fallback', {}, 'Fallback')}
                                                                </span>
                                                            )}
                                                            {alloc.isPickableBin && (
                                                                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                                                    {t('manager.modules.adminHost.putAwayDiag.badges.pickable', {}, 'Plukkbar')}
                                                                </span>
                                                            )}
                                                            {alloc.isSharedBin && (
                                                                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                                                    {t('manager.modules.adminHost.putAwayDiag.badges.shared', {}, 'Felles')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 text-sm">
                                                        <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                                                            {alloc.stepName || alloc.allocationReason}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Progress Bar */}
                                <div className="mt-4">
                                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                                        <span>{t('manager.modules.adminHost.putAwayDiag.allocationRate', {}, 'Fordelingsgrad')}</span>
                                        <span>{((simulation.totalAllocated / simulation.requestedQuantity) * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                                            style={{ width: `${Math.min((simulation.totalAllocated / simulation.requestedQuantity) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Steps Timeline */}
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">{t('manager.modules.adminHost.putAwayDiag.validationSteps', {}, 'Valideringssteg')}</h3>
                            <div className="space-y-2">
                                {simulation.steps.map((step, idx) => (
                                    <div key={idx} className={`flex items-start gap-3 p-4 rounded-lg border-2 ${
                                        step.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                                    }`}>
                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${
                                            step.passed ? 'bg-green-600' : 'bg-red-600'
                                        } text-white font-semibold`}>
                                            {step.stepNumber}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className={`font-semibold ${
                                                step.passed ? 'text-green-900' : 'text-red-900'
                                            }`}>
                                                {step.stepName}
                                            </h4>
                                            <p className={`text-sm mt-1 ${
                                                step.passed ? 'text-green-700' : 'text-red-700'
                                            }`}>
                                                {step.description}
                                            </p>
                                            <div className={`text-sm font-medium mt-2 ${
                                                step.passed ? 'text-green-800' : 'text-red-800'
                                            }`}>
                                                {t('manager.modules.adminHost.putAwayDiag.resultLabel', { '0': step.result }, `Resultat: ${step.result}`)}
                                            </div>
                                        </div>
                                        {step.passed ? (
                                            <CheckCircle className="w-6 h-6 text-green-600" />
                                        ) : (
                                            <XCircle className="w-6 h-6 text-red-600" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Bin Evaluations - split into accepted and rejected */}
                        {simulation.binsEvaluated && simulation.binsEvaluated.length > 0 && (() => {
                            const sorted = [...simulation.binsEvaluated].sort((a, b) => a.evaluationOrder - b.evaluationOrder);
                            const accepted = sorted.filter(b => b.accepted);
                            const rejected = sorted.filter(b => !b.accepted);

                            return (
                                <>
                                    {/* Accepted bins */}
                                    <details className="mt-4" open>
                                        <summary className="cursor-pointer font-semibold text-gray-900 mb-3 flex items-center gap-2 hover:text-blue-600">
                                            <MapPin className="w-5 h-5 text-green-600" />
                                            {t('manager.modules.adminHost.putAwayDiag.detailedBinEval', { '0': accepted.length }, `Detaljert lokasjonsevaluering (${accepted.length} lokasjoner)`)}
                                        </summary>
                                        <div className="space-y-2 mt-3">
                                            {accepted.map((bin, idx) => (
                                                <div key={idx} className="p-3 rounded-lg border bg-green-50 border-green-200">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono font-semibold text-gray-900">
                                                                {bin.binCode}
                                                            </span>
                                                            {bin.stcZoneID && (
                                                                <span className="text-xs text-gray-500">
                                                                    (Sone {bin.stcZoneID})
                                                                </span>
                                                            )}
                                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                                            {bin.stepName && (
                                                                <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                                                                    {bin.stepName}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                <span title={t('manager.modules.adminHost.putAwayDiag.table.stockMaxCapacity', {}, 'Beholdning / Maks kapasitet')}>
                                                                    {bin.binQuantity} / {bin.binMaxQuantity}
                                                                </span>
                                                                <span className={`font-medium ${bin.availableCapacity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                                    {t('manager.modules.adminHost.putAwayDiag.table.freeParen', { '0': bin.availableCapacity }, `(ledig: ${bin.availableCapacity})`)}
                                                                </span>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                {bin.isPickableBin && (
                                                                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                                                        {t('manager.modules.adminHost.putAwayDiag.badges.pick', {}, 'Plukk')}
                                                                    </span>
                                                                )}
                                                                {bin.isSharedBin && (
                                                                    <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                                                        {t('manager.modules.adminHost.putAwayDiag.badges.shared', {}, 'Felles')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-xs text-gray-400">
                                                                {t('manager.modules.adminHost.putAwayDiag.table.priLabel', { '0': bin.putAwayPriorityNo }, `Pri: ${bin.putAwayPriorityNo}`)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-sm text-blue-600 mt-1">
                                                        {bin.considerationReason}
                                                    </div>
                                                </div>
                                            ))}
                                            {accepted.length === 0 && (
                                                <div className="p-3 text-sm text-gray-500 italic">
                                                    {t('manager.modules.adminHost.putAwayDiag.noBinsAccepted', {}, 'Ingen lokasjoner ble akseptert av algoritmen.')}
                                                </div>
                                            )}
                                        </div>
                                    </details>

                                    {/* Rejected bins - flat list, collapsed by default */}
                                    {rejected.length > 0 && (
                                        <details className="mt-3">
                                            <summary className="cursor-pointer font-medium text-gray-700 mb-2 flex items-center gap-2 hover:text-red-600 transition-colors">
                                                <XCircle className="w-5 h-5 text-red-400" />
                                                <span>{t('manager.modules.adminHost.putAwayDiag.showRejected', {}, 'Vis avviste lokasjoner')}</span>
                                                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-semibold">
                                                    {t('manager.modules.adminHost.putAwayDiag.rejectedCount', { '0': rejected.length }, `${rejected.length} avvist`)}
                                                </span>
                                            </summary>
                                            <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-100 border-b border-gray-200">
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700 w-10">#</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('manager.modules.adminHost.putAwayDiag.table.group', {}, 'Gruppe')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('warehouse.general.zone', {}, 'Sone')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('manager.modules.adminHost.putAwayDiag.badges.shared', {}, 'Felles')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('warehouse.general.item', {}, 'Vare')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('manager.modules.adminHost.putAwayDiag.table.others', {}, 'Andre')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('manager.modules.adminHost.putAwayDiag.table.capacity', {}, 'Kapasitet')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('manager.modules.adminHost.putAwayDiag.table.pri', {}, 'Pri')}</th>
                                                            <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('manager.modules.adminHost.putAwayDiag.table.result', {}, 'Resultat')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {rejected.map((bin, idx) => (
                                                            <tr key={idx} className="hover:bg-red-50/50 transition-colors">
                                                                <td className="px-3 py-1.5 text-xs text-gray-400">{idx + 1}</td>
                                                                <td className="px-3 py-1.5 font-mono font-medium text-gray-800">
                                                                    {bin.binCode}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-gray-500">
                                                                    {bin.binGrpNo || '–'}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-gray-500">
                                                                    {bin.stcZoneID || '–'}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs">
                                                                    {bin.sharedBinYesNo
                                                                        ? <span className="text-purple-600 font-medium">{t('manager.modules.adminHost.putAwayDiag.yes', {}, 'Ja')}</span>
                                                                        : <span className="text-gray-400">{t('manager.modules.adminHost.putAwayDiag.no', {}, 'Nei')}</span>}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-gray-500">
                                                                    {bin.binQuantity > 0 ? bin.binQuantity : '–'}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs">
                                                                    {bin.otherItemsTotalQuantity > 0
                                                                        ? <span className="text-orange-600 font-medium">{bin.otherItemsTotalQuantity}</span>
                                                                        : <span className="text-gray-400">–</span>}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-gray-500">
                                                                    {bin.binMaxQuantity}
                                                                    <span className={`ml-1 ${bin.availableCapacity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                                        {t('manager.modules.adminHost.putAwayDiag.table.freeSuffix', { '0': bin.availableCapacity }, `(${bin.availableCapacity} ledig)`)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-gray-400">
                                                                    {bin.putAwayPriorityNo}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-red-600">
                                                                    <XCircle className="w-3 h-3 inline mr-1" />
                                                                    {bin.rejectionReason}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {/* Accepted bins at the end — the chosen location(s) */}
                                                        {accepted.map((bin, idx) => (
                                                            <tr key={`acc-${idx}`} className="bg-green-50 font-medium">
                                                                <td className="px-3 py-1.5 text-xs text-green-700">{rejected.length + idx + 1}</td>
                                                                <td className="px-3 py-1.5 font-mono font-bold text-green-800">
                                                                    {bin.binCode}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.binGrpNo || '–'}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.stcZoneID || '–'}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.sharedBinYesNo ? t('manager.modules.adminHost.putAwayDiag.yes', {}, 'Ja') : t('manager.modules.adminHost.putAwayDiag.no', {}, 'Nei')}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.binQuantity}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.otherItemsTotalQuantity}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.binMaxQuantity}
                                                                    <span className="ml-1">{t('manager.modules.adminHost.putAwayDiag.table.freeSuffix', { '0': bin.availableCapacity }, `(${bin.availableCapacity} ledig)`)}</span>
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    {bin.putAwayPriorityNo}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-green-700">
                                                                    <CheckCircle className="w-3 h-3 inline mr-1" />
                                                                    {t('manager.modules.adminHost.putAwayDiag.chosen', { '0': bin.considerationReason }, `Valgt — ${bin.considerationReason}`)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </details>
                                    )}
                                </>
                            );
                        })()}

                        {/* Final Result */}
                        <div className={`p-4 rounded-lg border-2 ${
                            simulation.wouldUseFallbackLocation ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                        }`}>
                            <div className="flex items-center gap-3">
                                {simulation.wouldUseFallbackLocation ? (
                                    <AlertCircle className="w-6 h-6 text-red-600" />
                                ) : (
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                )}
                                <div>
                                    <h4 className={`font-semibold ${
                                        simulation.wouldUseFallbackLocation ? 'text-red-900' : 'text-green-900'
                                    }`}>
                                        {simulation.wouldUseFallbackLocation
                                            ? t('manager.modules.adminHost.putAwayDiag.partialPlacement', { '0': simulation.remainingUnallocated }, `Delvis plassering - ${simulation.remainingUnallocated} enheter til [X]`)
                                            : t('manager.modules.adminHost.putAwayDiag.completePlacement', { '0': simulation.proposedDistribution?.filter(a => !a.isFallback).length || 1 }, `Komplett plassering på ${simulation.proposedDistribution?.filter(a => !a.isFallback).length || 1} lokasjon(er)`)
                                        }
                                    </h4>
                                    {simulation.wouldUseFallbackLocation && simulation.fallbackReason && (
                                        <p className="text-sm text-red-700 mt-1">
                                            {t('manager.modules.adminHost.putAwayDiag.reasonLabel', { '0': simulation.fallbackReason }, `Årsak: ${simulation.fallbackReason}`)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderRecommendationsPanel = () => {
        if (!results || !results.actionItems || results.actionItems.length === 0) return null;

        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                    onClick={() => toggleSection('recommendations')}
                    className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Lightbulb className="w-6 h-6 text-yellow-600" />
                        <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminHost.putAwayDiag.recommendations', {}, 'Anbefalinger')}</h2>
                    </div>
                    {expandedSections.recommendations ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                </button>

                {expandedSections.recommendations && (
                    <div className="p-6 pt-0">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <p className="text-blue-900 font-medium">{results.recommendation}</p>
                        </div>
                        <div className="space-y-2">
                            {results.actionItems.map((action, idx) => (
                                <div key={idx} className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-600 text-white text-sm font-semibold flex-shrink-0">
                                        {idx + 1}
                                    </div>
                                    <p className="text-gray-900 flex-1">{action}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-lg">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-white hover:text-purple-100 mb-4 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    {t('common.buttons.back', {}, 'Tilbake')}
                </button>
                <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-8 h-8" />
                    <h1 className="text-2xl font-bold">{t('manager.modules.adminHost.putAwayDiag.title', {}, 'Put-Away Diagnostikk')}</h1>
                </div>
                <p className="text-purple-100">
                    {t('manager.modules.adminHost.putAwayDiag.subtitle', {}, 'Analyser og diagnostiser put-away-algoritmen for å identifisere konfigurasjonsproblemer')}
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-medium">{t('manager.modules.adminHost.putAwayDiag.errorLabel', {}, 'Feil:')}</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* Input Form */}
            {renderInputForm()}

            {/* Results */}
            {results && (
                <>
                    {renderSummaryBar()}
                    {renderMasterDataPanel()}
                    {renderAlgorithmPanel()}
                    {renderRecommendationsPanel()}
                </>
            )}
        </div>
    );
};
